import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, catchError, map, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  IAgoraRTCClient,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteVideoTrack,
  UID,
  ILocalVideoTrack,
  ILocalAudioTrack,
} from 'agora-rtc-sdk-ng';
import { Stream } from '../models/stream.model';
import { AuthService } from '../services/auth.service';
import { getCurrentUserId } from '../helpers/utils';
import { SecureStorageService } from '../services/secure-storage.service';

interface StreamResponse {
  data: Stream[];
  status: string;
}

interface StreamData {
  title: string;
  description: string;
  /** 1 = gratuit, 0 = payant (défaut: 1) */
  free?: number;
  /** Prix en USD (si payant) */
  price?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AgoraService {
  private readonly baseApiUrl: string = environment.apiUrl;
  private readonly streamsUrl = `${this.baseApiUrl}/streams`;
  private readonly appId = '92ecc3d9867b4110be610a59ffec8685';
    
  // Map pour stocker la correspondance UID -> ID utilisateur
  private uidToUserIdMap = new Map<UID, number>();

  /** Token lu à chaque requête (évite un Bearer vide après connexion post-instanciation du service). */
  private getHttpOptions(): { headers: HttpHeaders } {
    let token = this.secureStorage.getItem<string>('token');
    if (token) {
      try {
        token = JSON.parse(token);
      } catch {
        token = token.replace(/"/g, '');
      }
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return { headers: new HttpHeaders(headers) };
  }

  private client: IAgoraRTCClient | null = null;
  private localAudioTrack: IMicrophoneAudioTrack | null = null;
  private localVideoTrack: ILocalVideoTrack | null = null;
  private viewerCount = new BehaviorSubject<number>(0);
  private streams = new BehaviorSubject<Stream[]>([]);
  private remoteVideoTrack = new Subject<{track: IRemoteVideoTrack, uid: UID}>();
  private viewerCountSubject = new BehaviorSubject<number>(0);
  private viewersSubject = new BehaviorSubject<any[]>([]);
  private viewerMap = new Map<number, any>();
  private remoteUsers: { [uid: number]: { uid: UID, hasAudio: boolean, hasVideo: boolean } } = {};
  private videoSourceChanged = new Subject<void>();
  private channelName: string = '';
  private uid: number = 0;
  private role: 'host' | 'audience' = 'audience';
  private options: any = {
    mode: 'live',
    codec: 'vp8' as 'vp8',
    role: 'audience',
    gatewayDomains: ['173.249.51.26:32080'],
    connectGatewayWithoutDomain: true,
    joinGatewayUse443portOnly: true
  };

  private agoraRTCModule: any | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private secureStorage: SecureStorageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (!isPlatformBrowser(this.platformId)) return;
    void this.initBrowserOnly();
  }

  private async initBrowserOnly(): Promise<void> {
    await this.getAgoraRTC();
    this.loadStreams();
    const AgoraRTC = this.agoraRTCModule;
    this.client = AgoraRTC.createClient(this.options);
    this.setupEventListeners();
  }

  private async getAgoraRTC(): Promise<any> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('AgoraRTC est disponible uniquement côté navigateur (SSR désactivé).');
    }
    if (this.agoraRTCModule) return this.agoraRTCModule;
    const mod: any = await import('agora-rtc-sdk-ng');
    this.agoraRTCModule = mod?.default ?? mod;
    return this.agoraRTCModule;
  }

  private setupEventListeners(): void {
    if (!this.client) return;
    this.client.on('user-published', async (user, mediaType) => {
      await this.client.subscribe(user, mediaType);
      console.log('Subscribe success');

      if (mediaType === 'video') {
        this.remoteVideoTrack.next({ track: user.videoTrack, uid: user.uid });
        this.videoSourceChanged.next();
      }
      if (mediaType === 'audio') {
        user.audioTrack?.play();
      }
    });

    this.client.on('user-unpublished', async (user, mediaType) => {
      await this.client.unsubscribe(user, mediaType);
      console.log('Unsubscribe from user success');

      if (mediaType === 'video') {
        this.remoteVideoTrack.next(null);
        this.videoSourceChanged.next();
      }
    });

    this.client.on('user-left', (user) => {
      console.log('User left:', user.uid);
      if (user.videoTrack) {
        this.remoteVideoTrack.next(null);
        this.videoSourceChanged.next();
      }
    });

    // Écouter les changements de source vidéo
    this.client.on('video-source-changed', () => {
      console.log('Source vidéo changée');
      this.videoSourceChanged.next();
    });
  }

  onVideoSourceChanged(): Observable<void> {
    return this.videoSourceChanged.asObservable();
  }

  subscribeToVideoSourceChanges(): void {
    // Cette méthode est appelée pour s'assurer que nous sommes abonnés aux changements de source vidéo
    console.log('Abonnement aux changements de source vidéo');
  }

  // Méthodes pour la gestion des streams
  getStreams(): Observable<Stream[]> {
    return this.http.get<StreamResponse>(this.streamsUrl, this.getHttpOptions()).pipe(
      map(response => {
        if (!response || !response.data) {
          throw new Error('Format de réponse invalide');
        }
        return response.data.map(stream => ({
          ...stream,
          createdAt: new Date(stream.created_at),
          updatedAt: new Date(stream.updated_at)
        }));
      }),
      catchError(error => {
        console.error('Erreur lors de la récupération des streams:', error);
        return throwError(() => new Error('Impossible de charger les streams. Veuillez réessayer plus tard.'));
      })
    );
  }

  getLastStream(): Observable<Stream[]> {
    return this.http.get<StreamResponse>(`${this.baseApiUrl}/last-streams`, this.getHttpOptions()).pipe(
      map(response => response.data)
    );
  }

  loadStreams(): void {
    this.getStreams().subscribe({
      next: (streams) => this.streams.next(streams),
      error: (error) => console.error('Erreur lors du chargement des streams:', error)
    });
  }

  createStream(data: StreamData): Observable<Stream> {
    const body = {
      title: data.title,
      description: data.description,
      free: data.free ?? 1,
      price: data.price ?? 0,
    };
    return this.http.post<Stream>(`${this.baseApiUrl}/streams`, body, this.getHttpOptions()).pipe(
      map(response => {
        return response;
      })
    );
  }

  /** Vérifie si l'utilisateur connecté a déjà payé pour ce live. GET /streams/{slug}/has-paid */
  hasPaidForStream(slug: string): Observable<{ has_paid: boolean }> {
    return this.http.get<{ has_paid: boolean }>(
      `${this.baseApiUrl}/streams/${slug}/has-paid`,
      this.getHttpOptions()
    );
  }

  updateStream(slug: string, streamData: Partial<Stream>): Observable<Stream> {
    return this.http.put<Stream>(`${environment.apiUrl}/streams/${slug}`, streamData, this.getHttpOptions()).pipe(
      map(stream => ({
        ...stream,
        createdAt: new Date(stream.created_at),
        updatedAt: new Date(stream.updated_at)
      }))
    );
  }

  endStream(slug: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/streams/${slug}/end`, {}, this.getHttpOptions()).pipe(
      catchError(() => this.http.delete<void>(`${environment.apiUrl}/streams/${slug}`, this.getHttpOptions()))
    );
  }

  deleteStream(slug: string): Observable<void> {
    return this.endStream(slug);
  }

  getStreamBySlug(slug: string): Observable<Stream> {
    return this.http.get<any>(`${environment.apiUrl}/streams/${slug}`, this.getHttpOptions()).pipe(
      map((response) => {
        const stream = response?.data ?? response;
        return {
          ...stream,
          createdAt: stream?.created_at ? new Date(stream.created_at) : undefined,
          updatedAt: stream?.updated_at ? new Date(stream.updated_at) : undefined,
        } as Stream;
      })
    );
  }

  // Alias pour getStreamBySlug pour compatibilité
  getStreamInfo(slug: string): Observable<Stream> {
    return this.getStreamBySlug(slug);
  }

  // Initialiser le client Agora pour le broadcaster
  async initializeBroadcaster(channelName: string): Promise<void> {
    try {
      const AgoraRTC = await this.getAgoraRTC();
      // Vérifier si l'utilisateur est connecté
      const currentUserId = getCurrentUserId();
      if (!currentUserId) {
        throw new Error('Utilisateur non connecté');
      }

      // Nettoyer les ressources existantes si nécessaire
      this.cleanup();

      // Créer un nouveau client avec le rôle "host"
      this.client = AgoraRTC.createClient({ 
        mode: 'live', 
        codec: 'vp8'
      });
      
      // Définir explicitement le rôle comme "host"
      await this.client.setClientRole('host');
      
      console.log('Client Agora créé avec le rôle "host"');
      
      // Ajouter des écouteurs d'événements pour le débogage
      this.client.on('connection-state-change', (curState, prevState) => {
        console.log('Connection state changed:', prevState, '->', curState);
      });

      this.client.on('error', (error) => {
        console.error('Client error:', error.code, error.message);
      });

      // Ajouter des écouteurs pour les spectateurs
      this.client.on('user-joined', async (user) => {
        console.log('Spectateur rejoint:', user.uid);
        // Mettre à jour le nombre de spectateurs
        this.updateViewerCount();
        
        // Récupérer les informations de l'utilisateur
        try {
          const currentUser = JSON.parse(this.secureStorage.getItem<string>('user') || '{}');
          const viewerData = {
            uid: user.uid,
            user_id: currentUser.id || getCurrentUserId(),
            joinedAt: new Date().toISOString()
          };
          const viewerInfo = await this.getUserInfo(user.uid, viewerData);
          this.addViewer(user.uid, viewerInfo);
        } catch (error) {
          console.error('Erreur lors de la récupération des informations du viewer:', error);
        }
      });

      this.client.on('user-left', (user) => {
        console.log('Spectateur parti:', user.uid);
        this.removeViewer(user.uid);
        this.updateViewerCount();
      });

      // Configurer les options de connexion
      AgoraRTC.enableLogUpload();
      AgoraRTC.setLogLevel(1); // INFO level

      // Rejoindre le canal
      const uid = await this.client.join(
        this.appId,
        channelName,
        null,
        null
      );
      console.log('Joined channel with UID:', uid);

      try {
        // Créer et initialiser les pistes audio et vidéo
        const [videoTrack, audioTrack] = await this.createLocalTracks();
        this.localVideoTrack = videoTrack;
        this.localAudioTrack = audioTrack;
        console.log('Tracks created successfully');

        // Publier les pistes
        if (this.localAudioTrack && this.localVideoTrack) {
          await this.client.publish([this.localAudioTrack, this.localVideoTrack]);
          console.log('Tracks published successfully');
        }
      } catch (mediaError) {
        console.error('Error creating media tracks:', mediaError);
        throw mediaError;
      }
    } catch (error) {
      console.error('Error in initializeBroadcaster:', error);
      this.cleanup();
      throw error;
    }
  }

  async createLocalTracks(): Promise<[ICameraVideoTrack, IMicrophoneAudioTrack]> {
    try {
      const AgoraRTC = await this.getAgoraRTC();
      const videoTrack = await AgoraRTC.createCameraVideoTrack();
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      this.localVideoTrack = videoTrack;
      this.localAudioTrack = audioTrack;
      return [videoTrack, audioTrack];
    } catch (error) {
      console.error('Erreur lors de la création des tracks locaux:', error);
      throw error;
    }
  }

  // Mettre à jour le nombre de spectateurs
  private updateViewerCount(): void {
    if (this.client) {
      const count = this.client.remoteUsers.length;
      console.log('Mise à jour du nombre de spectateurs:', count);
      this.viewerCountSubject.next(count);
      this.viewerCount.next(count);
    }
  }

  // Méthode pour ajouter un viewer avec son ID utilisateur
  addViewer(viewerId: UID, viewerData: any) {
    const numericId = Number(viewerId);
    console.log('Ajout d\'un viewer:', { viewerId: numericId, viewerData });
    
    // Vérifier si viewerData est undefined ou null
    if (!viewerData) {
      console.warn('viewerData est undefined ou null, création d\'un objet par défaut');
      viewerData = {
        uid: numericId,
        userId: numericId,
        joinedAt: new Date().toISOString()
      };
    }
    
    // Récupérer l'ID de l'utilisateur actuel
    const currentUserId = getCurrentUserId();
    console.log('ID de l\'utilisateur actuel:', currentUserId);
    
    // Si l'utilisateur est connecté, utiliser son ID
    if (currentUserId) {
      viewerData.userId = currentUserId;
      viewerData.user_id = currentUserId; // Pour la compatibilité avec getUserInfo
      console.log('Utilisation de l\'ID utilisateur connecté:', currentUserId);
      
      // Stocker l'ID utilisateur dans le localStorage pour une utilisation ultérieure
      localStorage.setItem('current_user_id', currentUserId.toString());
    } else {
      // Si l'utilisateur n'est pas connecté, utiliser l'ID temporaire
      viewerData.userId = viewerData.uid || numericId;
      viewerData.user_id = viewerData.userId; // Pour la compatibilité avec getUserInfo
      console.log('Utilisation de l\'ID temporaire pour le viewer:', viewerData.userId);
    }
    
    // S'assurer que l'uid est correctement défini
    if (!viewerData.uid) {
      viewerData.uid = numericId;
    }
    
    // Ne pas remplacer les viewers existants, mais ajouter ou mettre à jour
    if (!this.viewerMap.has(numericId)) {
      this.viewerMap.set(numericId, viewerData);
      console.log('Nouveau viewer ajouté:', numericId);
    } else {
      console.log('Viewer existant mis à jour:', numericId);
      // Conserver les informations existantes si elles sont plus complètes
      const existingData = this.viewerMap.get(numericId);
      const updatedData = { ...existingData, ...viewerData };
      
      // S'assurer que l'userId est conservé s'il existe déjà
      if (existingData.userId && !viewerData.userId) {
        updatedData.userId = existingData.userId;
      }
      
      // S'assurer que user_id est défini pour la compatibilité avec getUserInfo
      if (!updatedData.user_id) {
        updatedData.user_id = updatedData.userId;
      }
      
      this.viewerMap.set(numericId, updatedData);
    }
    
    this.updateViewers();
    this.updateViewerCount(); // Mettre à jour le compteur après l'ajout d'un viewer
  }

  // Méthode pour supprimer un viewer
  removeViewer(viewerId: UID) {
    const numericId = Number(viewerId);
    console.log('Suppression d\'un viewer:', numericId);
    this.viewerMap.delete(numericId);
    this.updateViewers();
    this.updateViewerCount(); // Mettre à jour le compteur après la suppression d'un viewer
  }

  // Mettre à jour la liste des spectateurs
  private async updateViewers(): Promise<void> {
    try {
      console.log('Début de la mise à jour des viewers');
      const viewersArray = Array.from(this.viewerMap.entries()).map(([uid, data]) => ({
        uid,
        ...data
      }));
      console.log('Liste des viewers à mettre à jour:', viewersArray);

      const updatedViewers = await Promise.all(
        viewersArray.map(async (viewer) => {
          try {
            console.log('Mise à jour des informations pour le viewer:', viewer.uid);
            const userInfo = await this.getUserInfo(viewer.uid, viewer);
            console.log('Informations utilisateur récupérées:', userInfo);
            return {
              ...viewer,
              ...userInfo
            };
          } catch (error) {
            console.error(`Erreur lors de la mise à jour des informations pour le viewer ${viewer.uid}:`, error);
            return {
              ...viewer,
              prenom: `Spectateur ${viewer.uid}`,
              profile_picture: 'assets/images/avatar.jpeg'
            };
          }
        })
      );

      console.log('Liste finale des viewers mise à jour:', updatedViewers);
      this.viewersSubject.next(updatedViewers);
      this.viewerCountSubject.next(updatedViewers.length);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des viewers:', error);
    }
  }

  // Méthode pour initialiser un viewer
  async initializeViewer(channelName: string, userId: number): Promise<void> {
    try {
      const AgoraRTC = await this.getAgoraRTC();
      if (!this.client) {
        this.client = AgoraRTC.createClient({ 
          mode: 'rtc', 
          codec: 'vp8',
          role: 'audience'
        });

        this.client.on('user-joined', (user) => {
          console.log('Utilisateur rejoint:', user.uid);
          this.updateViewerCount();
        });

        this.client.on('user-left', (user) => {
          console.log('Utilisateur parti:', user.uid);
          this.removeViewer(user.uid);
        });

        // Ajouter l'écouteur pour les publications de médias
        this.client.on('user-published', async (user, mediaType) => {
          await this.client?.subscribe(user, mediaType);
          
          if (mediaType === 'video') {
            const remoteVideoTrack = user.videoTrack;
            if (remoteVideoTrack) {
              this.remoteVideoTrack.next({ track: remoteVideoTrack, uid: user.uid });
            }
          }
          if (mediaType === 'audio') {
            const remoteAudioTrack = user.audioTrack;
            remoteAudioTrack?.play();
          }
        });
      }

      const uid = await this.client.join(
        this.appId,
        channelName,
        null,
        null
      );

      // Synchroniser les viewers
      await this.syncViewers(channelName);

      // Stocker l'ID utilisateur dans viewerData
      const viewerData = {
        userId: userId,
        uid: uid,
        joinedAt: new Date().toISOString()
      };

      console.log('Données du viewer avant ajout:', viewerData);

      // Ajouter le viewer avec son ID utilisateur
      this.addViewer(uid, viewerData);

      // Informer le serveur que l'utilisateur a rejoint le stream
      try {
        await this.http.get(`${this.baseApiUrl}/streams/${channelName}/viewers`, this.getHttpOptions()).toPromise();
        console.log('Information de jointure envoyée au serveur');
      } catch (error) {
        console.error('Erreur lors de l\'envoi de l\'information de jointure au serveur:', error);
      }

      console.log('Viewer initialisé avec UID:', uid, 'et userId:', userId);
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du viewer:', error);
      throw error;
    }
  }

  // Nettoyer les ressources
  private cleanup(): void {
    try {
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
        this.localAudioTrack = null;
      }
      if (this.localVideoTrack) {
        this.localVideoTrack.close();
        this.localVideoTrack = null;
      }
      if (this.client) {
        this.client.removeAllListeners();
      }
      console.log('Cleanup completed successfully');
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  // Arrêter le streaming
  async stopStreaming(): Promise<void> {
    try {
      if (this.localAudioTrack) {
        this.localAudioTrack.close();
      }
      if (this.localVideoTrack) {
        this.localVideoTrack.close();
      }
      if (this.client) {
        await this.client.leave();
      }
    } catch (error) {
      console.error('Erreur lors de l\'arrêt du streaming:', error);
      throw error;
    }
  }

  // Obtenir la piste vidéo locale
  getLocalVideoTrack(): ILocalVideoTrack | null {
    return this.localVideoTrack;
  }

  // Obtenir la piste audio locale
  getLocalAudioTrack(): IMicrophoneAudioTrack | null {
    return this.localAudioTrack;
  }

  // Obtenir la piste vidéo distante
  getRemoteVideoTrack(): IRemoteVideoTrack | null {
    if (this.client && this.client.remoteUsers.length > 0) {
      // Parcourir tous les utilisateurs distants pour trouver une piste vidéo
      for (const remoteUser of this.client.remoteUsers) {
        if (remoteUser.videoTrack) {
          console.log('Piste vidéo trouvée pour l\'utilisateur:', remoteUser.uid);
          return remoteUser.videoTrack;
        }
      }
    }
    
    console.warn('Aucune piste vidéo distante disponible');
    return null;
  }

  // Observer les pistes vidéo distantes
  onRemoteVideoTrack(): Observable<{track: IRemoteVideoTrack, uid: UID}> {
    return this.remoteVideoTrack.asObservable();
  }

  // Méthodes pour la gestion des spectateurs
  getViewerCount(): Observable<number> {
    return this.viewerCountSubject.asObservable();
  }

  getViewers(): Observable<any[]> {
    return this.viewersSubject.asObservable();
  }

  private async getUserInfo(uid: UID, viewerData: any): Promise<any> {
    try {
      console.log('Récupération des informations utilisateur pour UID:', uid);
      console.log('Données du viewer:', viewerData);

      // Vérifier si nous avons déjà les informations utilisateur dans viewerData
      if (viewerData?.user) {
        console.log('Informations utilisateur trouvées dans viewerData:', viewerData.user);
        return {
          uid: uid,
          name: viewerData.user.prenom || viewerData.user.name || 'Utilisateur',
          avatar: viewerData.user.profile_picture || 'assets/images/avatar.jpeg'
        };
      }

      // Si l'utilisateur est le current user, utiliser ses informations
      const currentUser = this.authService.getCurrentUser();
      if (currentUser && currentUser.id === viewerData.user_id) {
        console.log('Utilisation des informations de l\'utilisateur actuel:', currentUser);
        return {
          uid: uid,
          name: currentUser.prenom || currentUser.name || 'Utilisateur',
          avatar: currentUser.profile_picture || 'assets/images/avatar.jpeg'
        };
      }

      // Vérifier si user_id est défini avant de faire la requête API
      if (!viewerData.user_id) {
        console.warn('Pas d\'ID utilisateur défini pour le viewer:', uid);
        return {
          uid: uid,
          name: `Spectateur ${uid}`,
          avatar: 'assets/images/avatar.jpeg'
        };
      }

      // Faire la requête API avec l'ID utilisateur
      console.log('Requête API pour l\'utilisateur avec ID:', viewerData.user_id);
      const response = await this.http.get(`${environment.apiUrl}/users/${viewerData.user_id}`).toPromise();
      console.log('Réponse API pour les informations utilisateur:', response);

      if (response && (response as any).data) {
        const userInfo = (response as any).data;
        return {
          uid: uid,
          name: userInfo.prenom || userInfo.name || 'Utilisateur',
          avatar: userInfo.profile_picture || 'assets/images/avatar.jpeg'
        };
      }

      // Valeurs par défaut si aucune information n'est trouvée
      console.warn('Aucune information utilisateur trouvée, utilisation des valeurs par défaut');
      return {
        uid: uid,
        name: `Spectateur ${uid}`,
        avatar: 'assets/images/avatar.jpeg'
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des informations utilisateur:', error);
      return {
        uid: uid,
        name: `Spectateur ${uid}`,
        avatar: 'assets/images/avatar.jpeg'
      };
    }
  }

  // Méthode pour rejoindre un stream
  joinStream(slug: string): Observable<any> {
    console.log('Tentative de rejoindre le stream:', slug);
    return this.http.post(`${this.baseApiUrl}/streams/${slug}/join`, {}, this.getHttpOptions()).pipe(
      catchError(error => {
        console.error('Erreur lors de la tentative de rejoindre le stream:', error);
        return throwError(() => error);
      })
    );
  }

  // Méthode pour quitter un stream
  leaveStream(slug: string): Observable<any> {
    console.log('Tentative de quitter le stream:', slug);
    return this.http.post(`${this.baseApiUrl}/streams/${slug}/leave`, {}, this.getHttpOptions()).pipe(
      catchError(error => {
        console.error('Erreur lors de la tentative de quitter le stream:', error);
        return throwError(() => error);
      })
    );
  }

  setMute(isMuted: boolean): void {
    if (this.localAudioTrack) {
      this.localAudioTrack.setEnabled(!isMuted);
    }
  }

  setVideoEnabled(isEnabled: boolean): void {
    if (this.localVideoTrack) {
      this.localVideoTrack.setEnabled(isEnabled);
    }
  }

  setScreenSharing(isEnabled: boolean): void {
    if (isEnabled) {
      this.startScreenShare();
    } else {
      this.stopScreenShare();
    }
  }

  setCameraEnabled(isEnabled: boolean): void {
    if (isEnabled) {
      this.startCamera();
    } else {
      this.stopCamera();
    }
  }

  setBeautyEffect(isEnabled: boolean): void {
    if (this.localVideoTrack) {
      if (isEnabled) {
        this.localVideoTrack.setBeautyEffect(true);
      } else {
        this.localVideoTrack.setBeautyEffect(false);
      }
    }
  }

  async startScreenShare(): Promise<void> {
    try {
      const AgoraRTC = await this.getAgoraRTC();
      // Arrêter la piste vidéo actuelle si elle existe
      if (this.localVideoTrack) {
        await this.client?.unpublish(this.localVideoTrack);
        this.localVideoTrack.close();
      }

      const screenTrack = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: 'high_quality'
      });
      this.localVideoTrack = Array.isArray(screenTrack) ? screenTrack[0] : screenTrack;
      await this.client.publish(this.localVideoTrack);
    } catch (error) {
      console.error('Erreur lors du démarrage du partage d\'écran:', error);
      throw error;
    }
  }

  async stopScreenShare(): Promise<void> {
    try {
      if (this.localVideoTrack) {
        await this.client.unpublish(this.localVideoTrack);
        this.localVideoTrack.close();
        await this.startCamera();
      }
    } catch (error) {
      console.error('Erreur lors de l\'arrêt du partage d\'écran:', error);
      throw error;
    }
  }

  async startCamera(): Promise<void> {
    try {
      const AgoraRTC = await this.getAgoraRTC();
      if (!this.client) {
        this.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      }
      this.localVideoTrack = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: 'high_quality'
      });
      await this.client.publish(this.localVideoTrack);
    } catch (error) {
      console.error('Erreur lors du démarrage de la caméra:', error);
      throw error;
    }
  }

  async stopCamera(): Promise<void> {
    try {
      if (this.localVideoTrack) {
        await this.client.unpublish(this.localVideoTrack);
        this.localVideoTrack.close();
      }
    } catch (error) {
      console.error('Erreur lors de l\'arrêt de la caméra:', error);
      throw error;
    }
  }

  // Synchroniser les viewers entre les clients
  async syncViewers(channelName: string): Promise<void> {
    try {
      console.log('Synchronisation des viewers pour le canal:', channelName);
      
      // Récupérer les viewers depuis le serveur avec une requête GET
      const response = await this.http.get<any>(`${this.baseApiUrl}/streams/${channelName}/viewers`, this.getHttpOptions()).toPromise();
      console.log('Réponse du serveur pour les viewers:', response);
      
      if (response?.data) {
        // Vider la map des viewers actuels
        this.viewerMap.clear();
        
        // Vérifier si data est un tableau ou un objet
        let viewersData = [];
        
        if (Array.isArray(response.data)) {
          viewersData = response.data;
        } else if (response.data.viewers && Array.isArray(response.data.viewers)) {
          viewersData = response.data.viewers;
        } else if (response.data.viewer) {
          viewersData = [response.data.viewer];
        } else if (typeof response.data === 'object') {
          // Si data est un objet, essayer d'extraire les viewers
          const dataObj = response.data;
          if (dataObj.user_id && dataObj.uid) {
            viewersData = [dataObj];
          } else {
            // Parcourir les propriétés de l'objet pour trouver des viewers
            for (const key in dataObj) {
              if (dataObj[key] && typeof dataObj[key] === 'object' && (dataObj[key].user_id || dataObj[key].userId) && dataObj[key].uid) {
                viewersData.push(dataObj[key]);
              }
            }
          }
        }
        
        console.log('Données des viewers extraites:', viewersData);
        
        // Mettre à jour la map des viewers
        viewersData.forEach(viewer => {
          if (viewer.uid) {
            const numericId = Number(viewer.uid);
            // S'assurer que l'ID utilisateur est correctement défini
            const viewerData = {
              ...viewer,
              userId: viewer.user_id || viewer.userId, // Gérer les deux formats possibles
              uid: numericId
            };
            console.log('Ajout du viewer avec données:', viewerData);
            this.viewerMap.set(numericId, viewerData);
          }
        });
        
        // Mettre à jour le nombre de viewers et la liste des viewers
        this.updateViewers();
      }
    } catch (error) {
      console.error('Erreur lors de la synchronisation des viewers:', error);
    }
  }
}